// Voice input — MediaRecorder → POST /transcribe (Gemini), replacing browser
// SpeechRecognition (P2 design §2.4): Chrome's built-in engine needs Google's
// speech servers and fails with `network` on several of the team's browsers.
// Push-to-toggle: click to record, click to stop; the transcript lands in the
// composer for review before send (nothing auto-submits). Text arrives a few
// seconds after stop — server round trip, not streaming.

import { useCallback, useEffect, useRef, useState } from "react";
import { transcribeAudio } from "../lib/api";

const MAX_SECONDS = 120; // hard stop — keeps clips under the 5MB server cap

function pickMime(): string {
  if (typeof MediaRecorder === "undefined") return "";
  for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"]) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = () => reject(new Error("Could not read the recording"));
    r.readAsDataURL(blob);
  });
}

export function useSpeechInput(onFinalText: (text: string) => void): {
  supported: boolean;
  listening: boolean;
  /** Transcript in flight — recording stopped, text not back yet. */
  transcribing: boolean;
  /** Why the last session failed, else null. */
  error: string | null;
  toggle: () => void;
} {
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onTextRef = useRef(onFinalText);
  onTextRef.current = onFinalText;
  const supported =
    typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && pickMime() !== "";

  const stopRecorder = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    const rec = recRef.current;
    recRef.current = null;
    setListening(false);
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch { /* already stopped */ }
    }
    rec?.stream.getTracks().forEach((t) => t.stop()); // release the mic indicator
  }, []);

  const toggle = useCallback(() => {
    if (recRef.current) { stopRecorder(); return; } // stop → onstop transcribes
    setError(null);
    const mimeType = pickMime();
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      const rec = new MediaRecorder(stream, { mimeType });
      const chunks: Blob[] = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mimeType });
        if (blob.size === 0) return;
        setTranscribing(true);
        blobToBase64(blob)
          .then((b64) => transcribeAudio(b64, mimeType))
          .then((text) => { if (text) onTextRef.current(text); })
          .catch((e) => {
            console.error("[speech] transcribe failed:", e instanceof Error ? e.message : String(e));
            setError(e instanceof Error ? e.message : "Transcription failed");
          })
          .finally(() => setTranscribing(false));
      };
      rec.onerror = () => {
        console.error("[speech] recorder error");
        setError("Recording failed");
        stopRecorder();
      };
      rec.start();
      recRef.current = rec;
      setListening(true);
      timerRef.current = setTimeout(stopRecorder, MAX_SECONDS * 1000);
    }).catch((e) => {
      console.error("[speech] getUserMedia failed:", e instanceof Error ? e.name : String(e));
      setError(
        e instanceof Error && (e.name === "NotAllowedError" || e.name === "SecurityError")
          ? "Microphone blocked — allow mic access for this site in the browser"
          : e instanceof Error && e.name === "NotFoundError"
            ? "No microphone found"
            : "Could not start the microphone",
      );
    });
  }, [stopRecorder]);

  // Release the mic if the component unmounts mid-recording.
  useEffect(() => () => {
    const rec = recRef.current;
    recRef.current = null;
    if (rec) {
      rec.ondataavailable = null;
      rec.onstop = null;
      try { rec.stop(); } catch { /* already stopped */ }
      rec.stream.getTracks().forEach((t) => t.stop());
    }
  }, []);

  return { supported, listening, transcribing, error, toggle };
}
